import { useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import ApiDatabase from 'server';
import UseContext from 'hooks/useContext';
import { ChevronLeft } from 'assets/icons';
import SlideOverHeader from 'components/SlideOver/component/header';
import SlideOverBody from 'components/SlideOver/component/body';
import SlideOverFooter from 'components/SlideOver/component/footer';
import StyledInput from 'components/styledInput/StyledInput';
import Button from 'components/button';
import Banner from 'components/banner/banner';
import {
  COMPANY_ADMIN_ROLE,
  companyRoleDescription,
  companyRoleOptions,
} from 'constants/companyRoles';
import { validateContactForm } from 'helper/companyContactForm';

// errorCode from the api → [field to highlight, message key]
const API_ERRORS = {
  EMAIL_INVALID: ['email', 'emailInvalid'],
  EMAIL_ALREADY_USED: ['email', 'emailTaken'],
  FIRSTNAME_INVALID: ['firstName', 'required'],
  LASTNAME_INVALID: ['lastName', 'required'],
  ROLE_INVALID: ['role', 'required'],
  ADMIN_ROLE_TAKEN: ['role', 'adminTaken'],
};

const PENDING_VALIDATION = 0;

const CompanyContactEditView = ({ companyInfo, member, onBack, onSaved }) => {
  const { language } = UseContext();
  const t = (key) => language.getValue(`companyContacts.edit.${key}`, '');
  const [form, setForm] = useState({
    firstName: member.user?.firstname ?? '',
    lastName: member.user?.lastname ?? '',
    email: member.user?.email ?? '',
    role: String(member.role ?? ''),
  });
  const [errors, setErrors] = useState({});
  const [isSaving, setIsSaving] = useState(false);

  // The api refuses a second administrator whatever the other entry is, one
  // whose account is gone included; the warnings only count administrators
  // with an account, since an entry without one receives no reminder.
  const otherAdmins = useMemo(
    () =>
      (companyInfo?.employees ?? []).filter(
        (entry) =>
          Number(entry.role) === COMPANY_ADMIN_ROLE &&
          entry.user?._id !== member.user?._id,
      ),
    [companyInfo, member],
  );
  const otherAdminWithAccount = otherAdmins.find((entry) => entry.user);
  const isCompanyAdmin = Number(member.role) === COMPANY_ADMIN_ROLE;
  const normalizedEmail = form.email.trim().toLowerCase();
  // The warning is about the last administrator: with another one left, the
  // reminders of the company still reach someone.
  const isLastAdminLeaving =
    isCompanyAdmin &&
    !otherAdminWithAccount &&
    Number(form.role) !== COMPANY_ADMIN_ROLE;
  const isEmailChanged = normalizedEmail !== (member.user?.email ?? '');
  const isPending = member.user?.validation === PENDING_VALIDATION;

  // Never a message with an empty name: the holder can be an entry without an
  // account, or unknown to this screen when only the api saw them.
  const adminTakenText = () => {
    const name = [
      otherAdminWithAccount?.user?.firstname,
      otherAdminWithAccount?.user?.lastname,
    ]
      .filter(Boolean)
      .join(' ');
    if (name) return t('errors.adminTaken').replace('{name}', name);
    if (otherAdmins.length) return t('errors.adminTakenByMissingAccount');
    return t('errors.adminTakenUnknown');
  };

  const errorText = (key) => {
    if (!key) return undefined;
    if (key === 'adminTaken') return adminTakenText();
    return t(`errors.${key}`);
  };

  const update = (field) => (event) => {
    const { value } = event.target;
    setForm((previous) => ({ ...previous, [field]: value }));
    setErrors((previous) => ({ ...previous, [field]: undefined }));
  };

  const save = () => {
    const clientErrors = validateContactForm(form, {
      // A legacy company can have two administrators and the api lets each of
      // them keep the role: an administrator keeping it adds none, so only
      // someone else taking the role is refused.
      otherAdminExists: otherAdmins.length > 0 && !isCompanyAdmin,
    });
    if (Object.keys(clientErrors).length) {
      setErrors(clientErrors);
      return;
    }
    setIsSaving(true);
    ApiDatabase.postEditCompanyMember(
      {
        idCompany: companyInfo._id,
        idEmployee: member.user._id,
        employee: {
          firstName: form.firstName.trim(),
          lastName: form.lastName.trim(),
          email: normalizedEmail,
          role: Number(form.role),
        },
      },
      (data) => {
        setIsSaving(false);
        // Saved either way; false means the old link is dead and the new one
        // never left, which the admin can fix with the resend action.
        if (data.invitationResent === false) {
          toast.warning(t('savedInvitationFailed'));
        } else {
          toast.success(t('saved'));
        }
        onSaved(data.employee);
      },
      (err) => {
        setIsSaving(false);
        if (err?.name === 'CanceledError') return;
        const errorCode = err?.response?.data?.errorCode;
        // Removed meanwhile: retrying cannot succeed, so the screen closes.
        if (errorCode === 'MEMBER_NOT_FOUND') {
          toast.error(t('errors.memberNotFound'));
          onBack();
          return;
        }
        const known = API_ERRORS[errorCode];
        if (known) {
          setErrors({ [known[0]]: known[1] });
          return;
        }
        toast.error(t('errors.generic'));
      },
    );
  };

  return (
    <>
      <SlideOverHeader hide={onBack} hideShow={false}>
        <div className={'text-sm mb-3'}>
          <button
            type={'button'}
            className={'border rounded p-2'}
            onClick={onBack}
          >
            <ChevronLeft wh={20} color={'#111827'} />
            <span className={'sr-only'}>{t('back')}</span>
          </button>
        </div>
        <div>{t('title')}</div>
        <div className={'text-gray-500 text-sm pt-0.5 font-normal'}>
          {t('subtitle')}
        </div>
      </SlideOverHeader>
      <SlideOverBody>
        <div className={'flex flex-col gap-4 text-sm'}>
          <div className={'grid grid-cols-2 gap-3'}>
            <StyledInput
              type={'text'}
              label={t('firstName')}
              value={form.firstName}
              onChange={update('firstName')}
              error={errorText(errors.firstName)}
            />
            <StyledInput
              type={'text'}
              label={t('lastName')}
              value={form.lastName}
              onChange={update('lastName')}
              error={errorText(errors.lastName)}
            />
          </div>
          <div className={'flex flex-col gap-1'}>
            <StyledInput
              type={'email'}
              label={t('email')}
              value={form.email}
              onChange={update('email')}
              error={errorText(errors.email)}
            />
            {isEmailChanged && !errors.email ? (
              <div className={'text-gray-500 text-xs'}>
                {t(isPending ? 'pendingEmailChange' : 'activeEmailHint')}
              </div>
            ) : null}
          </div>
          <StyledInput
            type={'select'}
            label={t('role')}
            list={companyRoleOptions(language)}
            value={form.role}
            onChange={update('role')}
            error={errorText(errors.role)}
            hint={companyRoleDescription(language, form.role)}
          />
          {isLastAdminLeaving ? (
            <Banner type={'warning'}>{t('lastAdminWarning')}</Banner>
          ) : null}
        </div>
      </SlideOverBody>
      <SlideOverFooter>
        <Button size={'LG'} color={'SECONDARY'} onClick={onBack}>
          {t('cancel')}
        </Button>
        <Button
          size={'LG'}
          color={isSaving ? 'DISABLED' : 'PRIMARY'}
          onClick={isSaving ? undefined : save}
        >
          {isSaving ? t('saving') : t('save')}
        </Button>
      </SlideOverFooter>
    </>
  );
};

export default CompanyContactEditView;
